import { Badge } from "@/components/ui/badge";
import { Briefcase, GraduationCap, Calendar, MapPin, Linkedin, Building2 } from "lucide-react";
import { useRef } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";

import { useState } from "react";

interface TimelineItem {
  type: 'education' | 'work';
  title: string;
  organization: string;
  companyUrl?: string;
  companyLogo?: string;
  period: string;
  location: string;
  description?: string[];
}

const timelineItems: TimelineItem[] = [
  {
    type: 'work',
    title: "Chief Technology Officer",
    organization: "ANTYK Technologies",
    companyUrl: "https://www.linkedin.com/company/antyk-technologies",
    companyLogo: "/company-logos/antyk.png",
    period: "July 2026 - Present",
    location: "Onsite - Chennai, Tamil Nadu, India",
    description: [],
  },
  {
    type: 'work',
    title: "Programmer Analyst Trainee",
    organization: "Cognizant",
    companyUrl: "https://www.linkedin.com/company/cognizant",
    companyLogo: "/company-logos/cognizant.jpg",
    period: "April 2026 - July 2026",
    location: "Onsite - Coimbatore, Tamil Nadu, India",
    description: [],
  },
  {
    type: 'work',
    title: "Product Development",
    organization: "Yoko Styles",
    companyUrl: "https://www.linkedin.com/company/yoko-styles",
    companyLogo: "/company-logos/yoko.jpg",
    period: "November 2025 - January 2026",
    location: "Remote - Coimbatore, Tamil Nadu, India",
    description: [
      "Developed a chatbot-based software plugin using a client-server architecture to retrieve garment data from a database.",
      "Integrated a large language model to process natural language queries and return relevant responses via APIs.",
      "Enabled the plugin to run and open directly within the host software environment.",
    ],
  },
  {
    type: 'work',
    title: "Software Developer Intern",
    organization: "Kuizzo",
    companyUrl: "https://www.linkedin.com/company/kuizzo",
    companyLogo: "/company-logos/kuizzo.jpg",
    period: "July 2025 - October 2025",
    location: "Remote - United States",
    description: [],
  },
];

const educationItems = [
  {
    title: "R.M.D Engineering College",
    degree: "B.Tech - Artificial Intelligence & Machine Learning",
    period: "2022 - Present",
    location: "Kavaraipettai, Chennai, India",
    highlights: [
      "1st Place - Science Day Project Expo",
      "1st Place - Intra-Department Hackathon",
      "3rd Place - IDEATHON 2023 (IIC & RMDEC-IDEA Lab)",
      "Selected for Smart India Hackathon 2023 & 2024",
    ],
  },
  {
    title: "Velammal Vidyashram CBSE",
    degree: "Computer Science",
    period: "2020 - 2022",
    location: "Surapet, Chennai, India",
    highlights: [],
  },
];

const CompanyLogo = ({ src, name }: { src?: string; name: string }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-sm font-medium text-foreground">
        {name.charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      className="h-8 w-8 rounded-md object-contain bg-white/5"
      onError={() => setFailed(true)}
    />
  );
};

const Timeline = () => {
  const timelineRef = useScrollReveal({ threshold: 0.1 });

  return (
    <section ref={timelineRef} id="journey" className="border-t border-border bg-muted/40 scroll-reveal">
      <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">06 - Journey</p>
        <h2 className="font-display text-4xl md:text-5xl leading-[1.05] text-foreground mb-16">
          Experience & education.
        </h2>

        <div className="grid md:grid-cols-12 gap-12">
          {/* Experience column */}
          <div className="md:col-span-7">
            <div className="flex items-center gap-2 mb-8">
              <Briefcase className="h-5 w-5 text-primary" />
              <h3 className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Experience</h3>
            </div>
            <div className="relative pl-6 border-l border-border space-y-10">
              {timelineItems.map((item, index) => (
                <div key={index} className="relative">
                  <span className="absolute -left-[29px] top-1.5 h-3 w-3 rounded-full bg-primary border-2 border-background"></span>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-muted-foreground">{item.period}</span>
                    <h4 className="text-lg font-medium text-foreground">{item.title}</h4>
                    <div className="flex items-center gap-2.5 mt-1.5">
                      <CompanyLogo src={item.companyLogo} name={item.organization} />
                      {item.companyUrl ? (
                        <a href={item.companyUrl} target="_blank" rel="noopener noreferrer" className="text-base font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
                          {item.organization}
                          <Linkedin className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-base font-medium text-muted-foreground">{item.organization}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin size={12} />
                      <span>{item.location}</span>
                    </div>
                    {item.description && item.description.length > 0 && (
                      <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground leading-relaxed list-disc list-inside">
                        {item.description.map((desc, i) => (
                          <li key={i}>{desc}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Education column */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-8">
              <GraduationCap className="h-5 w-5 text-primary" />
              <h3 className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Education</h3>
            </div>
            <div className="space-y-6">
              {educationItems.map((edu, index) => (
                <div key={index} className="border border-border rounded-xl p-6 bg-card">
                  <h4 className="text-base font-medium text-foreground">{edu.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{edu.degree}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Calendar size={12} />{edu.period}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={12} />{edu.location}</span>
                  </div>
                  {edu.highlights.length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground list-disc list-inside">
                      {edu.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Timeline;
